import { useState, useEffect, useRef } from 'react';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function ContentProductionPage() {
  useSEO({
    title: '콘텐츠 제작 - KR STUDIO | 전문 교육 콘텐츠 제작 서비스',
    description: 'KR스튜디오의 전문 콘텐츠 제작 서비스를 만나보세요. 기획부터 촬영, 편집, 후반작업까지 원스톱 교육 콘텐츠 제작 솔루션을 제공합니다.',
    keywords: '콘텐츠 제작, 교육 콘텐츠, 영상 제작, 이러닝 콘텐츠, 촬영 편집',
    canonical: '/content-production',
    schema: createServiceSchema(
      '콘텐츠 제작 서비스',
      '기획부터 촬영, 편집, 후반작업까지 원스톱 교육 콘텐츠 제작 솔루션'
    ),
  });

  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [counts, setCounts] = useState({ hours: 0, clients: 0, complaints: 0, rating: 0 });
  const statsRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        hours: Math.floor(24 * progress),
        clients: Math.floor(300 * progress),
        complaints: 0,
        rating: parseFloat((4.91 * progress).toFixed(2))
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounts({ hours: 24, clients: 300, complaints: 0, rating: 4.91 });
      }
    }, stepDuration);
  };

  const productionMethods = [
    {
      id: 'electronic-board',
      icon: 'ri-presentation-line',
      title: '전자칠판 강의',
      features: ['4K 고화질 전자칠판', '자연스러운 판서 효과', 'PPT 연동 자동 전환']
    },
    {
      id: 'chroma-key',
      icon: 'ri-camera-line',
      title: '크로마키 촬영',
      features: ['고급 색감 보정', '인물 중심 조명', '배경 합성 자유도']
    },
    {
      id: 'tablet',
      icon: 'ri-tablet-line',
      title: '태블릿 강의',
      features: ['실시간 필기 연동', '화면 분할 구성', '디지털 교안 활용']
    },
    {
      id: 'ai-content',
      icon: 'ri-robot-line',
      title: 'AI 콘텐츠',
      features: ['스크립트 기반 제작', 'AI 아나운서 활용', '다국어 자막 지원']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: '니즈 진단',
      day: '1일차',
      items: [
        { icon: 'ri-phone-line', text: '상담 전화 (30분)' },
        { icon: 'ri-file-list-line', text: '요구사항 분석서 작성' },
        { icon: 'ri-lightbulb-line', text: '맞춤 제작 방식 제안' },
        { icon: 'ri-money-dollar-circle-line', text: '투명한 견적서 제공' }
      ]
    },
    {
      step: '02',
      title: '파일럿 제작',
      day: '2-3일차',
      items: [
        { icon: 'ri-focus-line', text: '첫 강의 시범 촬영' },
        { icon: 'ri-palette-line', text: '강사 스타일 분석' },
        { icon: 'ri-edit-line', text: '포맷 확정 및 템플릿화' },
        { icon: 'ri-checkbox-circle-line', text: '고객 피드백 반영' }
      ]
    },
    {
      step: '03',
      title: '본 촬영',
      day: '4일차~',
      items: [
        { icon: 'ri-video-line', text: '전담 PD 매칭' },
        { icon: 'ri-map-pin-line', text: '동선 및 세팅 완료' },
        { icon: 'ri-clapperboard-line', text: '자연스러운 디렉팅' },
        { icon: 'ri-save-line', text: '실시간 백업 진행' }
      ]
    },
    {
      step: '04',
      title: '후반 작업',
      day: '당일',
      items: [
        { icon: 'ri-scissors-cut-line', text: '정밀 편집 작업' },
        { icon: 'ri-text', text: 'AI 자막 생성' },
        { icon: 'ri-contrast-line', text: '색보정 및 음향 정리' },
        { icon: 'ri-shield-check-line', text: '2단계 품질 검수' }
      ]
    },
    {
      step: '05',
      title: '납품',
      day: '24시간 내',
      items: [
        { icon: 'ri-inbox-line', text: '다양한 포맷 제공' },
        { icon: 'ri-link', text: 'LMS 직접 업로드' },
        { icon: 'ri-smartphone-line', text: '모바일 최적화' },
        { icon: 'ri-bar-chart-line', text: '시청 데이터 제공' }
      ]
    }
  ];

  const testimonials = [
    {
      organization: '서울대학교 온라인교육센터',
      rating: 5,
      content: '급하게 추진된 비대면 특강이었는데, 24시간 내 납품 시스템이 정말 강력했습니다. 교수님이 촬영 후 본인 강의 스타일이 더 살아난다고 감탄하셨어요.',
      author: '이러닝 기획팀 김혜진 팀장'
    },
    {
      organization: '한국생명존중희망재단',
      rating: 5,
      content: '촬영-편집-LMS 연결까지 전 과정을 KR 한 곳에서 해결할 수 있어 정말 편리했습니다. 크로마키 인물 색감이 자연스러워 강의 집중도가 높았다는 평가를 받았습니다.',
      author: '교육사업팀 박진우 과장'
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section with Video Background */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image (simulating video) */}
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/01721397da55af21878e201cee4469af/6847db7ca610dcc2a89a3acdc55d1499.jpeg"
            alt="Instructor teaching"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-white text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight">
            2위를 1위로 만드는<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-teal-400">
              콘텐츠의 비밀
            </span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 font-light text-gray-200">
            강사의 가치를 키우는 24시간 내 영상 제작 시스템
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact"
              className="hidden px-10 py-4 bg-secondary text-white font-bold text-lg hover:bg-blue-600 transition cursor-pointer text-center whitespace-nowrap inline-block"
            >
              지금 견적 받기
            </a>
            <a 
              href="#portfolio"
              className="hidden px-10 py-4 border-2 border-white text-white font-bold text-lg hover:bg-white hover:text-primary transition cursor-pointer text-center whitespace-nowrap inline-block"
            >
              포트폴리오 보기
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section with Counter Animation */}
      <section ref={statsRef} className="py-16 bg-primary text-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-black text-center mb-12">숫자로 보는 KR</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-black text-secondary mb-3">{counts.hours}</div>
              <div className="text-lg text-gray-300">평균 납품 시간</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-secondary mb-3">{counts.clients}+</div>
              <div className="text-lg text-gray-300">함께한 고객사</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-secondary mb-3">{counts.complaints}</div>
              <div className="text-lg text-gray-300">컴플레인 발생</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black text-secondary mb-3">{counts.rating}</div>
              <div className="text-lg text-gray-300">강사 만족도</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why KR Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-center mb-16 text-primary">왜 KR인가?</h2>
          
          <div className="max-w-5xl mx-auto space-y-12">
            {/* Differentiated Approach */}
            <div className="bg-white p-10 rounded-lg shadow-lg">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-focus-3-line text-2xl text-secondary"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-black mb-3 text-primary">차별화된 접근</h3>
                  <p className="text-xl font-bold text-secondary mb-3">"우리는 단순히 찍는 게 아니라, 강사를 연구합니다"</p>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                매 촬영마다 전담 PD가 배정되어 강사의 말투, 제스처, 시선 처리, 강의 리듬까지 분석합니다. 
                첫 파일럿 영상으로 강사님만의 최적화된 포맷을 찾아내고, 이후 모든 콘텐츠에 일관되게 적용합니다.
              </p>
            </div>

            {/* Speed Secret */}
            <div className="bg-white p-10 rounded-lg shadow-lg">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-line text-2xl text-secondary"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-black mb-3 text-primary">24시간 속도의 비밀</h3>
                  <p className="text-xl font-bold text-secondary mb-3">"빠른 게 아니라, 준비가 되어 있습니다"</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-secondary text-lg mt-1"></i>
                  <span className="text-base text-gray-700">사전 제작된 150개 템플릿 보유</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-secondary text-lg mt-1"></i>
                  <span className="text-base text-gray-700">5개 스튜디오 동시 운영 체제</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-secondary text-lg mt-1"></i>
                  <span className="text-base text-gray-700">자동화된 편집 워크플로우</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-secondary text-lg mt-1"></i>
                  <span className="text-base text-gray-700">실시간 렌더링 시스템</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-center mb-5 text-primary">제작 방식 선택</h2>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            강의 특성에 맞는 최적의 제작 방식을 선택하세요
          </p>
          
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Electronic Board (Main Featured) */}
            <div
              className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 h-[600px]"
            >
              <div className="absolute inset-0">
                <img 
                  src="https://static.readdy.ai/image/01721397da55af21878e201cee4469af/72c3ee392cb24e2b9b377745d9c40e1b.png"
                  alt="전자칠판 강의"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30 group-hover:from-black/85 group-hover:via-black/60 transition-all duration-500"></div>
              </div>
              
              <div className="relative h-full flex flex-col justify-end p-8">
                <div className="mb-4">
                  <span className="inline-flex items-center bg-secondary text-white px-4 py-2 rounded-full text-sm font-bold">
                    <i className="ri-star-fill mr-2"></i>
                    가장 인기있는 방식
                  </span>
                </div>
                
                <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-5">
                  <i className="ri-presentation-line text-3xl text-white"></i>
                </div>
                
                <h3 className="text-3xl font-black text-white mb-3">전자칠판 강의</h3>
                <p className="text-white/90 text-base mb-6 leading-relaxed">
                  4K 고화질 전자칠판으로 자연스러운 판서 효과와 PPT 연동을 제공합니다. 강사의 필기와 설명이 완벽하게 동기화되어 학습 효과를 극대화합니다.
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <i className="ri-hd-line text-xl text-secondary mb-2"></i>
                    <p className="text-white font-bold text-sm">4K 고화질 전자칠판</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <i className="ri-edit-line text-xl text-secondary mb-2"></i>
                    <p className="text-white font-bold text-sm">자연스러운 판서 효과</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <i className="ri-slideshow-line text-xl text-secondary mb-2"></i>
                    <p className="text-white font-bold text-sm">PPT 연동 자동 전환</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <i className="ri-save-line text-xl text-secondary mb-2"></i>
                    <p className="text-white font-bold text-sm">실시간 필기 저장</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Three Stacked Methods */}
            <div className="flex flex-col gap-8 h-[600px]">
              {/* Chroma Key */}
              <div
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 flex-1"
              >
                <div className="absolute inset-0">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/94c509df6ba544117a1463f717efa416.jpg"
                    alt="크로마키 촬영"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 group-hover:from-black/85 group-hover:via-black/70 transition-all duration-500"></div>
                </div>
                
                <div className="relative h-full flex items-center p-6">
                  <div className="flex-1">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-camera-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">크로마키 촬영</h3>
                    <p className="text-white/90 text-sm mb-3">고급 색감 보정과 배경 합성으로 프리미엄 콘텐츠 제작</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">고급 색감 보정</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">인물 중심 조명</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">배경 합성 자유도</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tablet */}
              <div
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 flex-1"
              >
                <div className="absolute inset-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=instructor%20teaching%20with%20digital%20tablet%20and%20stylus%20pen%20modern%20educational%20technology%20clean%20studio%20setup%20professional%20digital%20teaching%20environment%20tablet%20screen%20recording%20high%20quality&width=800&height=220&seq=method-tablet&orientation=landscape"
                    alt="태블릿 강의"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 group-hover:from-black/85 group-hover:via-black/70 transition-all duration-500"></div>
                </div>
                
                <div className="relative h-full flex items-center p-6">
                  <div className="flex-1">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-tablet-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">태블릿 강의</h3>
                    <p className="text-white/90 text-sm mb-3">실시간 필기 연동과 화면 분할로 효과적인 강의 진행</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">실시간 필기 연동</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">화면 분할 구성</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">디지털 교안 활용</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Content */}
              <div
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 flex-1"
              >
                <div className="absolute inset-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=futuristic%20AI%20virtual%20presenter%20holographic%20display%20modern%20technology%20interface%20automated%20content%20creation%20studio%20digital%20avatar%20professional%20broadcast%20quality%20clean%20high%20tech%20environment&width=800&height=220&seq=method-ai&orientation=landscape"
                    alt="AI 콘텐츠"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 group-hover:from-black/85 group-hover:via-black/70 transition-all duration-500"></div>
                </div>
                
                <div className="relative h-full flex items-center p-6">
                  <div className="flex-1">
                    <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3">
                      <i className="ri-robot-line text-2xl text-white"></i>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">AI 콘텐츠</h3>
                    <p className="text-white/90 text-sm mb-3">AI 아나운서와 자동 자막으로 빠르고 효율적인 제작</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">AI 아나운서</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">자동 자막 생성</span>
                      <span className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">빠른 제작 속도</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-center mb-5 text-primary">프로세스 상세</h2>
          <p className="text-center text-lg text-gray-600 mb-16 max-w-3xl mx-auto">
            체계적인 5단계 프로세스로 최고의 결과물을 보장합니다
          </p>
          
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {processSteps.map((step, index) => (
                <div key={step.step} className="relative group">
                  {/* Connector Line */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-1 bg-gradient-to-r from-secondary to-secondary/30"></div>
                  )}
                  
                  {/* Step Card */}
                  <div className="relative bg-gradient-to-br from-gray-50 to-white p-5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-secondary/20 h-full">
                    {/* Step Number Badge */}
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-14 h-14 bg-gradient-to-br from-secondary to-blue-600 rounded-full flex items-center justify-center text-white font-black text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    {/* Day Badge */}
                    <div className="mt-6 mb-3 text-center">
                      <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary font-bold rounded-full text-xs">
                        {step.day}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-black text-center mb-5 text-primary">
                      {step.title}
                    </h3>
                    
                    {/* Items */}
                    <div className="space-y-2">
                      {step.items.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2 group/item">
                          <div className="w-7 h-7 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:bg-secondary/20 transition">
                            <i className={`${item.icon} text-secondary text-xs`}></i>
                          </div>
                          <span className="text-xs text-gray-700 leading-relaxed pt-1">
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <section className="py-8 bg-gradient-to-br from-primary via-gray-900 to-primary">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-black text-center mb-3 text-white">고객 후기</h2>
          <p className="text-center text-base text-gray-300 mb-8">
            실제 고객사의 생생한 경험을 확인하세요
          </p>
          
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Testimonial 1 */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-2xl text-secondary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white blur-sm select-none">서울대학교 온라인교육센터</h3>
                    <p className="text-sm text-gray-400">
                      이러닝 기획팀 <span className="blur-sm select-none">김혜진</span> 팀장
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed text-sm">
                  "급하게 추진된 비대면 특강이었는데, 24시간 내 납품 시스템이 정말 강력했습니다. 교수님이 촬영 후 본인 강의 스타일이 더 살아난다고 감탄하셨어요."
                </p>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-2xl text-secondary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white blur-sm select-none">한국생명존중희망재단</h3>
                    <p className="text-sm text-gray-400">
                      교육사업팀 <span className="blur-sm select-none">박진우</span> 과장
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed text-sm">
                  "촬영-편집-LMS 연결까지 전 과정을 KR 한 곳에서 해결할 수 있어 정말 편리했습니다. 크로마키 인물 색감이 자연스러워 강의 집중도가 높았다는 평가를 받았습니다."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-5">지금 바로 시작하세요</h2>
          <p className="text-xl mb-10 text-gray-100">24시간 내 고품질 콘텐츠 제작을 경험해보세요</p>
          <a 
            href="tel:070-8656-8134"
            className="hidden px-12 py-5 bg-white text-primary font-bold text-xl hover:bg-gray-100 transition cursor-pointer inline-block whitespace-nowrap rounded-xl"
          >
            <i className="ri-phone-line mr-2"></i>
            070-8656-8134 전화하기
          </a>
        </div>
      </section>

      {/* Modal for Production Method Details */}
      {selectedMethod && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6"
          onClick={() => setSelectedMethod(null)}
        >
          <div 
            className="bg-white rounded-lg p-8 max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {productionMethods.find(m => m.id === selectedMethod) && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-3xl font-black text-primary">
                    {productionMethods.find(m => m.id === selectedMethod)?.title}
                  </h3>
                  <button
                    onClick={() => setSelectedMethod(null)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center cursor-pointer transition"
                  >
                    <i className="ri-close-line text-2xl text-gray-600"></i>
                  </button>
                </div>
                <div className="space-y-4">
                  {productionMethods.find(m => m.id === selectedMethod)?.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <i className="ri-checkbox-circle-fill text-secondary text-xl mt-1"></i>
                      <span className="text-lg text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <a 
                    href="#contact"
                    className="px-8 py-3 bg-secondary text-white font-bold hover:bg-blue-600 transition cursor-pointer inline-block whitespace-nowrap"
                  >
                    이 방식으로 견적 받기
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
      <QuickMenu />
    </div>
  );
}
